import type { GeneralApiError } from "@/api/custom_types";
import i18next from "i18next";
import { useCallback } from "react";
import { toast } from "react-toastify";

function useApiError() {
	/**
	 * A 5xx often carries no typed body - sometimes not an object at all - and
	 * `"type" in error` throws on those, which killed the toast and made the
	 * request look like it had silently done nothing. The declared type stays
	 * GeneralApiError so react-query keeps inferring mutation errors from it.
	 */
	const getErrorMessage = useCallback((error: GeneralApiError) => {
		const fallback = i18next.t("error.unexpected");

		if (error && typeof error === "object" && "type" in error && error.type) {
			return i18next.t(`openApi.error.${error.type}`, {
				defaultValue: fallback,
			});
		}

		return fallback;
	}, []);

	const toastApiError = useCallback(
		(error: GeneralApiError) => {
			toast.error(getErrorMessage(error));
		},
		[getErrorMessage],
	);

	const getErrorInterface = useCallback(
		(error: GeneralApiError) => new Error(getErrorMessage(error)),
		[getErrorMessage],
	);

	return {
		toastApiError,
		getErrorMessage,
		getErrorInterface,
	};
}

export default useApiError;
